<template>
  <!-- Tunnel : appel en cours affiché en popup centré (le commercial n'est plus libre).
       Se réduit automatiquement en bandeau quand on va sur l'analyse du besoin,
       pour laisser travailler le commercial. -->
  <Teleport to="body">
    <Transition name="call-modal">
      <div v-if="callInProgress && !minimized && !besoinModalOpen" class="call-modal-overlay" role="dialog" aria-modal="true">
        <div class="call-banner call-banner--modal">
          <CallBannerHeader
            :call-duration="callDuration"
            :quote="quote"
            :quote-id="quoteId"
            :saving-call="savingCall"
            :call-direction="callDirection"
            :format-timer="formatTimer"
            @abandon-call="$emit('abandon-call')"
            @mark-no-answer="$emit('mark-no-answer')"
            @mark-not-interested="$emit('mark-not-interested')"
            @schedule-relance="$emit('schedule-relance')"
            @validate-info="$emit('validate-info')"
          />
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Bandeau minimisé : téléporté dans le conteneur des bandeaux du bas pour
       s'empiler avec la barre d'action de l'analyse du besoin (au lieu d'être masqué). -->
  <Teleport v-if="callInProgress && minimized" to="#bottom-bars-container">
    <div class="call-banner call-banner--bar">
      <CallBannerHeader
        :call-duration="callDuration"
        :quote="quote"
        :quote-id="quoteId"
        :saving-call="savingCall"
        :call-direction="callDirection"
        :format-timer="formatTimer"
        @abandon-call="$emit('abandon-call')"
        @mark-no-answer="$emit('mark-no-answer')"
        @mark-not-interested="$emit('mark-not-interested')"
        @schedule-relance="$emit('schedule-relance')"
        @validate-info="$emit('validate-info')"
      />
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import CallBannerHeader from './CallBannerHeader.vue'
import { useCallState } from '../../composables/calls/useCallState.js'

defineProps({
  callInProgress: {
    type: Boolean,
    required: true
  },
  callDuration: {
    type: Number,
    default: 0
  },
  quote: {
    type: Object,
    default: null
  },
  quoteId: {
    type: [String, Number],
    default: null
  },
  savingCall: {
    type: Boolean,
    default: false
  },
  callDirection: {
    type: String,
    default: 'sortant'
  },
  formatTimer: {
    type: Function,
    required: true
  }
})

const route = useRoute()

// Popup « Analyse du besoin » ouvert : on masque le bandeau d'appel (il réapparaît
// à la fermeture si l'appel est toujours en cours).
const { besoinModalOpen, savedCallId } = useCallState()

// L'analyse du besoin en mode page (aperçu / deep-link) : le tunnel doit céder la
// place (bandeau minimisé) pour laisser travailler le commercial.
// Le popup centré est fait pour la QUALIFICATION : tant qu'elle dure, le
// commercial n'a rien d'autre à faire. Dès que l'analyse du besoin est validée
// (l'appel a sa ligne), il enchaîne sur l'édition puis l'envoi du devis, et il
// lui faut la fiche : le bandeau se réduit alors en barre. Sans cela l'overlay
// réapparaissait par-dessus la fiche sans offrir de sortie vers l'envoi.
const minimized = computed(() =>
  /\/analyse-besoin\/?$/.test(route.path) || Boolean(savedCallId.value)
)

defineEmits([
  'abandon-call',
  'mark-no-answer',
  'mark-not-interested',
  'schedule-relance',
  'validate-info'
])
</script>

<style scoped>
/* ---- Mode tunnel : popup centré ---- */
.call-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-bg-overlay);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Sous les modales d'issue (base Modal, z-index 9999) pour qu'elles s'ouvrent par-dessus */
  z-index: var(--z-modal, 1050);
  padding: var(--spacing-md);
  overflow-y: auto;
}

.call-banner--modal {
  width: 100%;
  max-width: 820px;
  max-height: 90vh;
  background: var(--color-danger);
  color: var(--color-text-inverse);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ---- Mode minimisé : bandeau en bas de page ---- */
.call-banner--bar {
  position: relative;
  width: 100%;
  background: var(--color-danger);
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-md, 0 -2px 10px rgba(0, 0, 0, 0.2));
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: callSlideUp 0.3s ease-out;
}

@keyframes callSlideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

/* Transitions du popup tunnel */
.call-modal-enter-active,
.call-modal-leave-active {
  transition: opacity 0.25s ease;
}

.call-modal-enter-active .call-banner--modal,
.call-modal-leave-active .call-banner--modal {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}

.call-modal-enter-from,
.call-modal-leave-to {
  opacity: 0;
}

.call-modal-enter-from .call-banner--modal {
  transform: translateY(30px) scale(0.96);
  opacity: 0;
}

.call-modal-leave-to .call-banner--modal {
  transform: translateY(20px) scale(0.98);
  opacity: 0;
}

@media (max-width: 768px) {
  .call-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .call-banner--modal {
    max-width: 100%;
    max-height: 95vh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  }
}
</style>
